import { CopyOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, Row, Select } from 'antd';
import type { ProdutoLocalEstoque } from '@/features/produto-local-estoque/types/ProdutoLocalEstoque';
import type { ProdutoBodyPacoteBarra } from '@/features/produto/types/ProdutoBody';
import { InputNumber } from '@/components/input/InputNumber';
import { ProdutoLocalEstoqueSelect } from '@/features/produto-local-estoque/components/ProdutoLocalEstoqueSelect';
import { ProdutoPacoteBarraTipoPreco } from '../types/ProdutoPacoteBarra';
import { calcularPrecoVendaProdutoPacote } from '../util/preco';
import { ProdutoUnidadeSelect } from './ProdutoUnidadeSelect';

export function ProdutoPacoteBarraFormFields({
    unidadeEstoque,
    locaisEstoque,
    pro_preco_venda,
    pro_cod_barra,
}: {
    unidadeEstoque: number | string | null | undefined;
    locaisEstoque: ProdutoLocalEstoque[];
    pro_preco_venda: number;
    pro_cod_barra: string | null | undefined;
}) {
    const formPacote = Form.useFormInstance<ProdutoBodyPacoteBarra>();

    const pcb_multiplicador = Form.useWatch('pcb_multiplicador', formPacote) || 0;
    const pcb_valor = Form.useWatch('pcb_valor', formPacote) || 0;
    const pcb_tipo_preco = Form.useWatch('pcb_tipo_preco', formPacote);

    const showValor = typeof pcb_tipo_preco === 'number' && pcb_tipo_preco > 1;

    const showPreco =
        typeof pcb_tipo_preco === 'number' &&
        pcb_tipo_preco !== ProdutoPacoteBarraTipoPreco.PrecoPersonalizado;

    const { description, preco } = calcularPrecoVendaProdutoPacote({
        pcb_multiplicador,
        pcb_tipo_preco,
        pcb_valor,
        pro_preco_venda,
    });

    return (
        <Row gutter={[8, 8]}>
            <Col xs={12}>
                <Form.Item
                    getValueFromEvent={(e) => (e.target.checked ? 'S' : 'N')}
                    getValueProps={(e) => ({ checked: e === 'S', value: e === 'S' })}
                    label='Exibir no catálogo'
                    name='pcb_exibir_catalogo'
                    valuePropName='checked'
                >
                    <Checkbox />
                </Form.Item>
            </Col>

            <Col xs={12}>
                <Form.Item label='Unidade do estoque'>
                    <ProdutoUnidadeSelect disabled={true} value={unidadeEstoque} />
                </Form.Item>
            </Col>

            <Col xs={24}>
                <Form.Item
                    label='Local de estoque padrão'
                    name='pcb_estoque_idpk'
                    normalize={(value) => value ?? null}
                    tooltip={
                        <>
                            Informe qual será o <strong>local de estoque</strong> padrão utilizado
                            ao vender este pacote de barras.
                        </>
                    }
                >
                    <ProdutoLocalEstoqueSelect locais={locaisEstoque} />
                </Form.Item>
            </Col>

            <Col xs={12}>
                <Form.Item
                    label='Qtde a baixar do estoque'
                    name='pcb_multiplicador'
                    rules={[
                        {
                            message: 'Por favor, forneça o multiplicador',
                            required: true,
                        },
                    ]}
                    tooltip='Informe a quantidade que será baixada do estoque deste produto para cada unidade vendida deste pacote/barras.'
                >
                    <InputNumber decimalScale={2} />
                </Form.Item>
            </Col>

            <Col xs={12}>
                <Form.Item
                    label='Unidade desejada'
                    name='pcb_produto_unidade_idpk'
                    rules={[
                        {
                            message: 'Por favor, forneça a unidade',
                            required: true,
                        },
                    ]}
                >
                    <ProdutoUnidadeSelect />
                </Form.Item>
            </Col>

            <Col xs={12}>
                <Form.Item
                    help={
                        pro_cod_barra ? (
                            <div className='flex items-center gap-1'>
                                Cod. barras do produto: {pro_cod_barra}
                                <Button
                                    icon={<CopyOutlined />}
                                    onClick={() => {
                                        formPacote.setFieldValue('pcb_cod_barra', pro_cod_barra);
                                    }}
                                    size='small'
                                />
                            </div>
                        ) : null
                    }
                    label='Código de barras'
                    name='pcb_cod_barra'
                >
                    <Input />
                </Form.Item>
            </Col>

            <Col xs={12}>
                <Form.Item label='Descrição' name='pcb_descricao'>
                    <Input />
                </Form.Item>
            </Col>

            <Col xs={12}>
                <Form.Item label='Tipo de preço' name='pcb_tipo_preco'>
                    <Select
                        options={[
                            {
                                label: 'Igual ao preço do cadastro',
                                value: ProdutoPacoteBarraTipoPreco.IgualPrecoCadastro,
                            },
                            {
                                label: 'Acrescentar no preço do cadastro',
                                value: ProdutoPacoteBarraTipoPreco.AcrescentarPrecoCadastro,
                            },
                            {
                                label: 'Descontar no preço do cadastro',
                                value: ProdutoPacoteBarraTipoPreco.DescontarPrecoCadastro,
                            },
                            {
                                label: 'Preço personalizado do pacote',
                                value: ProdutoPacoteBarraTipoPreco.PrecoPersonalizado,
                            },
                        ]}
                    />
                </Form.Item>
            </Col>

            {showValor && (
                <Col xs={12}>
                    {(pcb_tipo_preco === ProdutoPacoteBarraTipoPreco.AcrescentarPrecoCadastro ||
                        pcb_tipo_preco === ProdutoPacoteBarraTipoPreco.DescontarPrecoCadastro) && (
                        <Form.Item
                            className='m-0'
                            help={description}
                            label='Valor'
                            name='pcb_valor'
                        >
                            <InputNumber decimalScale={2} min={0} type='%' />
                        </Form.Item>
                    )}

                    {pcb_tipo_preco === ProdutoPacoteBarraTipoPreco.PrecoPersonalizado && (
                        <Form.Item className='m-0' label='Valor' name='pcb_valor'>
                            <InputNumber decimalScale={4} type='$' />
                        </Form.Item>
                    )}
                </Col>
            )}

            {showPreco && (
                <Col xs={12}>
                    <Form.Item label='Preço de venda'>
                        <InputNumber decimalScale={4} disabled={true} type='$' value={preco} />
                    </Form.Item>
                </Col>
            )}

            <Col xs={12}>
                <Form.Item label='Quantidade de volumes' name='pcb_volume_qtde'>
                    <InputNumber />
                </Form.Item>
            </Col>

            <Col xs={12}>
                <Form.Item label='Peso líquido' name='pcb_peso_liquido'>
                    <InputNumber decimalScale={4} />
                </Form.Item>
            </Col>

            <Col xs={12}>
                <Form.Item label='Peso bruto' name='pcb_peso_bruto'>
                    <InputNumber decimalScale={4} />
                </Form.Item>
            </Col>
        </Row>
    );
}
